/**
 * Code Refactor v2 Executor
 *
 * Handles the 'code-refactor-v2' and 'pipeline-code-refactor-v2' node execution.
 * Calls the ModernizeIT API Layer /coderefactorv2 endpoint.
 *
 * API Flow (WORKER MODE):
 * 1. Verify upstream Java Analysis job is 'completed' (poll if needed)
 * 2. Verify upstream Service Generation job ID exists
 * 3. POST /coderefactorv2 -> returns job_id with status QUEUED
 * 4. Poll GET /jobs/{job_id} -> wait until status = 'completed'
 * 5. Extract results from job.output_data
 *
 * CRITICAL: Requires both java_analysis_job_id AND service_generation_job_id.
 * Java Analysis provides findings (via graph). Service Gen provides source (via S3).
 */

import { pollJobUntilComplete } from '../../utils/jobPoller.js'
import { getAuthHeaders, handleApiError } from './authHelper.js'

/**
 * Execute the code refactor v2 node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with job info and artifacts
 */
export async function execute(config, context, onProgress = () => {}, signal = null) {
  const apiBaseUrl = context.api_base_url

  onProgress(0, 'Preparing Code Refactor v2...')

  // Check for abort before starting
  if (signal?.aborted) {
    throw new Error('Execution cancelled')
  }

  // 1. Verify upstream Java Analysis job exists
  const jaJobId = context.jobs?.java_analysis_job_id
  if (!jaJobId) {
    throw new Error('Code Refactor v2 requires java_analysis_job_id from Java Analysis node. Run Java Analysis first.')
  }

  // 2. Verify upstream Service Generation job exists
  const svcgenJobId = context.jobs?.service_generation_job_id
  if (!svcgenJobId) {
    throw new Error('Code Refactor v2 requires service_generation_job_id from Service Generation node. Run Service Generation first.')
  }

  // 3. Verify upstream Java Analysis job is completed (poll if needed)
  console.log(`[CodeRefactorV2Executor] Verifying upstream Java Analysis job ${jaJobId} is completed...`)
  onProgress(2, 'Waiting for Java Analysis to complete...')

  try {
    await pollJobUntilComplete(jaJobId, apiBaseUrl, {
      maxWaitSeconds: 600,
      pollInterval: 1000,
      onProgress: (progress, message) => {
        const mappedProgress = 2 + (progress * 0.06)
        onProgress(mappedProgress, `Waiting for Java Analysis: ${message}`)
      },
      signal
    })
    console.log(`[CodeRefactorV2Executor] Upstream Java Analysis job ${jaJobId} is completed`)
  } catch (error) {
    throw new Error(`Upstream Java Analysis job failed: ${error.message}`)
  }

  onProgress(8, 'Java Analysis complete, starting Code Refactor v2...')

  // 4. Build the request body
  const requestBody = {
    scout_account_id: context.scout_account_id,
    application_name: context.application_name,
    user_id: context.user_id,
    java_analysis_job_id: jaJobId,
    svcgen_job_id: svcgenJobId,
    workflow_run_id: context.workflow_run_id || null,
  }

  onProgress(10, 'Submitting Code Refactor v2 job...')

  // 5. Submit the job
  let jobResponse
  try {
    const response = await fetch(`${apiBaseUrl}/coderefactorv2`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(requestBody),
      signal,
    })

    if (!response.ok) {
      await handleApiError(response)
    }

    jobResponse = await response.json()

    if (jobResponse.success === false) {
      throw new Error(jobResponse.error || 'Code Refactor v2 failed')
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Cannot connect to API at ${apiBaseUrl}. Is the server running?`)
    }
    throw error
  }

  const jobId = jobResponse.job_id
  if (!jobId) {
    throw new Error('API did not return a job_id')
  }

  console.log(`[CodeRefactorV2Executor] Job created: ${jobId}, polling until completed...`)
  onProgress(15, `Job started: ${jobId}`)

  // 6. Poll for completion (longer timeout — refactoring + compile may be slow)
  const completedJob = await pollJobUntilComplete(jobId, apiBaseUrl, {
    maxWaitSeconds: 900,
    pollInterval: 2000,
    onProgress: (progress, message) => {
      const mappedProgress = 15 + (progress * 0.80)
      onProgress(mappedProgress, message)
    },
    signal
  })

  console.log('[CodeRefactorV2Executor] Job completed, extracting results from output_data')
  onProgress(95, 'Extracting results...')

  // 7. Extract results from job.output_data
  const outputData = completedJob.output_data
  if (!outputData) {
    throw new Error('Job completed but has no output_data')
  }

  onProgress(100, 'Code Refactor v2 complete')

  // 8. Return formatted result
  return formatResult(jobId, outputData, context.jobs)
}

/**
 * Format the result object for consistent structure
 */
function formatResult(jobId, data, contextJobs = {}) {
  console.log('[CodeRefactorV2Executor] formatResult called with contextJobs:', contextJobs)

  const resultJobs = {
    ...contextJobs,
    code_refactor_v2_job_id: jobId,
  }

  console.log('[CodeRefactorV2Executor] Returning jobs:', resultJobs)

  return {
    // Job tracking — preserve upstream job IDs for downstream nodes
    jobs: resultJobs,

    // Status
    success: data.compile_success !== false,
    status: 'completed',

    // Summary statistics
    summary: data.summary || {},

    // Artifacts path (for downstream Pre-Flight/Packaging)
    artifacts_path: data.artifacts_path || '',

    // Graph loading results
    graph_loaded: data.graph_loaded || false,

    // Per-class results
    class_results: data.class_results || [],

    // Error info if any
    errors: data.errors || [],
  }
}

export default {
  execute,
}
